import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/api.service';
import employeeAuthService from '../services/employee-auth.service';
import { useNavigate } from 'react-router-dom';
import './EmployeeTodoList.css'; 

const pad2 = (n) => n.toString().padStart(2, '0');
const toYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const EmployeeTodoList = ({ employeeId }) => {
  const navigate = useNavigate();

  // State variables
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [entries, setEntries] = useState([]);
  const [customTasks, setCustomTasks] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [uploadingTaskId, setUploadingTaskId] = useState(null);
  const [removingItemImageId, setRemovingItemImageId] = useState(null);
  const [removingTaskImageId, setRemovingTaskImageId] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [showCompleted, setShowCompleted] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => toYMD(new Date()));
  const [activeTemplateTab, setActiveTemplateTab] = useState(null); // entryId

  // Refs for scroll position management
  const todoContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // API URL
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const getUploadsUrl = (imagePath) => {
    if (!imagePath) return '';
    if (/^https?:\/\//i.test(imagePath)) return imagePath;
    const base = String(API_URL || '').replace(/\/+$/, '');
    const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    if (normalizedPath.startsWith('/uploads/')) return `${base}${normalizedPath}`;
    return `${base}/uploads${normalizedPath}`;
  };

  const getActorDisplayName = (actor) => {
    if (!actor) return null;
    const fullName = `${actor.name || ''} ${actor.surname || ''}`.trim();
    if (fullName) return fullName;
    if (actor.username) return actor.username;
    return null;
  };

  const getCompletedByName = (obj) =>
    getActorDisplayName(obj?.completedByEmployee || obj?.completedByUser);

  const getImageUploadedByName = (obj) =>
    getActorDisplayName(obj?.imageUploadedByEmployee || obj?.imageUploadedByUser);

  // Save scroll position before updates
  const saveScrollPosition = () => {
    if (todoContainerRef.current) {
      scrollPositionRef.current = todoContainerRef.current.scrollTop;
    }
  };

  // Restore scroll position after render
  useEffect(() => {
    if (todoContainerRef.current && scrollPositionRef.current) {
      setTimeout(() => {
        if (todoContainerRef.current) {
          todoContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      }, 0);
    }
  }, [entries, customTasks]);
  
  // Helper to show success message
  const showSuccessMessage = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Handle API errors using useCallback for stability
  const handleApiError = useCallback((err, defaultMessage) => {
    console.error('API error:', err);
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      employeeAuthService.logout();
      navigate('/employee/login');
      return;
    }
    setError(err.response?.data?.error || defaultMessage || 'Bir hata oluştu.');
  }, [navigate]);

  // Fetch employee details, tasks, and entries on component mount
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      if (!employeeAuthService.isLoggedIn()) {
        navigate('/employee/login');
        return;
      }
      
      // Get employee info
      const empInfo = employeeAuthService.getUser();
      setEmployeeInfo(empInfo);
      
      // Fetch initial data for today's date
      fetchData(toYMD(new Date()));
    };

    checkAuthAndFetchData();
  }, [navigate, handleApiError]);

  // Function to fetch both entries and tasks for a specific date
  const fetchData = async (date) => {
    setLoading(true);
    setSelectedDate(date);
    setError(null);
    setSuccess(null);
    scrollPositionRef.current = 0; // Reset scroll position for new date
    
    try {
      // Fetch both entries and tasks in parallel
      await Promise.all([
        fetchEntriesForDay(date),
        fetchCustomTasksForDay(date)
      ]);
    } catch (err) {
      console.error('Error fetching data:', err);
      handleApiError(err, 'Günlük görevler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Fetch template-based entries for the selected day
  const fetchEntriesForDay = async (dateStr) => {
    setLoadingEntries(true);
    setEntries([]);
    try {
      const [year, month, day] = dateStr.split('-');
      const response = await apiService.get(`/api/todo/entries?year=${year}&month=${month}&day=${day}`);
      setEntries(response.data);
    } catch (err) {
      console.error('Error fetching template entries:', err);
      handleApiError(err, 'Şablon görevleri yüklenirken bir hata oluştu');
      setEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  };

  // Fetch custom tasks for the selected day
  const fetchCustomTasksForDay = async (dateStr) => {
    setLoadingTasks(true);
    setCustomTasks([]);
    try {
      const response = await apiService.get(`/api/tasks?date=${dateStr}`);
      setCustomTasks(response.data);
    } catch (err) {
      console.error('Error fetching custom tasks:', err);
      handleApiError(err, 'Özel görevler yüklenirken bir hata oluştu');
      setCustomTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Update a template-based todo entry item (with optimistic UI update)
  const handleUpdateEntryItem = async (entryItemId, isCompleted, itemNotes = null) => {
    saveScrollPosition();

    if (isCompleted === true) {
      const current = entries
        .flatMap((entry) => entry.items || [])
        .find((item) => item.id === entryItemId);

      const requiresImage = !!current?.item?.requires_image;
      const hasImage = !!current?.image_path;

      if (requiresImage && !hasImage) {
        setError('Bu görev için görsel yüklenmeden tamamlandı olarak işaretleyemezsiniz.');
        return;
      }
    }
    
    // Optimistic UI update
    setEntries(prevEntries => 
      prevEntries.map(entry => ({
        ...entry,
        items: entry.items.map(item => 
          item.id === entryItemId 
            ? { 
                ...item, 
                is_completed: isCompleted, 
                notes: itemNotes !== null ? itemNotes : item.notes,
                completed_at: isCompleted ? new Date().toISOString() : null
              }
            : item
        )
      }))
    );
    
    try {
      const requestBody = { is_completed: isCompleted };
      if (itemNotes !== null) {
        requestBody.notes = itemNotes;
      }
      const response = await apiService.put(`/api/todo/entry-items/${entryItemId}`, requestBody);
      const updatedItem = response.data;

      // Merge server-side fields (completedBy*, etc.) into the updated item
      setEntries(prevEntries =>
        prevEntries.map(entry => ({
          ...entry,
          items: (entry.items || []).map(item => (item.id === entryItemId ? { ...item, ...updatedItem } : item))
        }))
      );
    } catch (err) {
      console.error('Error updating entry item:', err);
      handleApiError(err, 'Görev güncellenirken bir hata oluştu');
      
      // On error, refetch to ensure data consistency
      fetchEntriesForDay(selectedDate);
    }
  };

  // Update custom task (with optimistic UI update)
  const handleUpdateCustomTask = async (taskId, updates) => {
    saveScrollPosition();
    
    // Optimistic UI update
    setCustomTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId 
          ? { ...task, ...updates, updated_at: new Date().toISOString() } 
          : task
      )
    );
    
    try {
      const response = await apiService.put(`/api/tasks/${taskId}`, updates);
      const updatedTask = response.data;

      setCustomTasks(prevTasks =>
        prevTasks.map(task => (task.id === taskId ? { ...task, ...updatedTask } : task))
      );
    } catch (err) {
      console.error('Error updating custom task:', err);
      handleApiError(err, 'Görev güncellenirken bir hata oluştu');
      
      // On error, refetch to ensure data consistency
      fetchCustomTasksForDay(selectedDate);
    }
  };

  // Upload an image for a template-based todo entry item
  const handleImageUpload = async (entryItemId, imageFile) => {
    saveScrollPosition();
    
    try {
      setUploadingItemId(entryItemId);
      if (!imageFile || !imageFile.type.startsWith('image/')) {
        setError('Lütfen geçerli bir görsel dosyası seçin (JPG, PNG, GIF, vb.)');
        setUploadingItemId(null);
        return;
      }
      
      const formData = new FormData();
      formData.append('image', imageFile);
      
      await apiService.post(`/api/todo/entry-items/${entryItemId}/upload`, formData);
      fetchEntriesForDay(selectedDate);
      showSuccessMessage('Görsel başarıyla yüklendi');
    } catch (err) {
      console.error('Error uploading image:', err);
      handleApiError(err, 'Görsel yüklenirken bir hata oluştu');
    } finally {
      setUploadingItemId(null);
    }
  };

  const handleRemoveEntryItemImage = async (entryItemId) => {
    saveScrollPosition();
    try {
      setRemovingItemImageId(entryItemId);
      await apiService.delete(`/api/todo/entry-items/${entryItemId}/image`);

      setEntries((prev) =>
        prev.map((entry) => ({
          ...entry,
          items: (entry.items || []).map((item) =>
            item.id === entryItemId ? { ...item, image_path: null, is_completed: false, completed_at: null } : item
          )
        }))
      );

      showSuccessMessage('Görsel kaldırıldı');
    } catch (err) {
      console.error('Error removing entry item image:', err);
      handleApiError(err, 'Görsel kaldırılırken bir hata oluştu');
      fetchEntriesForDay(selectedDate);
    } finally {
      setRemovingItemImageId(null);
    }
  };

  // Handle custom task image upload
  const handleTaskImageUpload = async (taskId, imageFile) => {
    saveScrollPosition();
    
    try {
      setUploadingTaskId(taskId);
      if (!imageFile || !imageFile.type.startsWith('image/')) {
        setError('Lütfen geçerli bir görsel dosyası seçin');
        setUploadingTaskId(null);
        return;
      }
      
      const formData = new FormData();
      formData.append('image', imageFile);
      
      await apiService.post(`/api/tasks/${taskId}/upload`, formData);
      fetchCustomTasksForDay(selectedDate);
      showSuccessMessage('Görev görseli başarıyla yüklendi');
    } catch (err) {
      console.error('Error uploading task image:', err);
      handleApiError(err, 'Görev görseli yüklenirken bir hata oluştu');
    } finally {
      setUploadingTaskId(null);
    }
  };

  const handleRemoveTaskImage = async (taskId) => {
    saveScrollPosition();
    try {
      setRemovingTaskImageId(taskId);
      await apiService.delete(`/api/tasks/${taskId}/image`);
      setCustomTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, image_path: null } : task)));
      showSuccessMessage('Görsel kaldırıldı');
    } catch (err) {
      console.error('Error removing task image:', err);
      handleApiError(err, 'Görsel kaldırılırken bir hata oluştu');
      fetchCustomTasksForDay(selectedDate);
    } finally {
      setRemovingTaskImageId(null);
    }
  };

  // Handle date change
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    fetchData(newDate);
  };

  // Get the total number of incomplete tasks
  const getTotalIncompleteTasks = () => {
    const entryItems = entries.reduce((total, entry) => {
      return total + entry.items.filter(item => !item.is_completed).length;
    }, 0);
    
    const customTasksCount = customTasks.filter(task => !task.is_completed).length;
    
    return entryItems + customTasksCount;
  };

  // Get the total number of completed tasks
  const getTotalCompletedTasks = () => {
    const entryItems = entries.reduce((total, entry) => {
      return total + entry.items.filter(item => item.is_completed).length;
    }, 0);
    
    const customTasksCount = customTasks.filter(task => task.is_completed).length;
    
    return entryItems + customTasksCount;
  };

  // Filter custom tasks based on showCompleted
  const getFilteredCustomTasks = () => {
    if (showCompleted) return customTasks;
    return customTasks.filter((task) => !task.is_completed);
  };

  const getEntryCounts = (entry) => {
    const total = Array.isArray(entry?.items) ? entry.items.length : 0;
    const completed = (entry?.items || []).filter((i) => i.is_completed).length;
    const open = total - completed;
    return { total, completed, open };
  };

  const getVisibleEntryItems = (entry) => {
    const items = Array.isArray(entry?.items) ? entry.items : [];
    if (!showCompleted) return items.filter((i) => !i.is_completed);
    return items;
  };

  const filteredEntries = entries
    .map((entry) => ({ ...entry, items: getVisibleEntryItems(entry) }))
    .filter((entry) => (entry.items || []).length > 0);

  const filteredCustomTasks = getFilteredCustomTasks();

  const templateTabs = entries
    .map((entry) => {
      const title = entry.template?.title || 'İsimsiz Şablon';
      const counts = getEntryCounts(entry);
      const visibleCount = getVisibleEntryItems(entry).length;
      return { id: entry.id, title, counts, visibleCount };
    })
    .sort((a, b) => a.title.localeCompare(b.title, 'tr'));

  useEffect(() => {
    if (templateTabs.length === 0) {
      if (activeTemplateTab !== null) setActiveTemplateTab(null);
      return;
    }

    const firstVisible = templateTabs.find((t) => t.visibleCount > 0) || null;
    const stillVisible =
      activeTemplateTab !== null && templateTabs.some((t) => String(t.id) === String(activeTemplateTab) && t.visibleCount > 0);

    if (!stillVisible) {
      setActiveTemplateTab(firstVisible ? firstVisible.id : null);
    }
  }, [activeTemplateTab, templateTabs]);

  const renderEntryCard = (entry) => (
    <div key={entry.id} className="employee-entry-card">
      <div className="employee-entry-header">
        <h4>{entry.template?.title || 'İsimsiz Şablon'}</h4>
        {entry.notes && <div className="employee-entry-notes">{entry.notes}</div>}
      </div>

      <div className="employee-entry-items">
        {(entry.items || []).length === 0 ? (
          <div className="employee-no-tasks employee-no-tasks--inner">Bu şablonda gösterilecek görev yok.</div>
        ) : (
          entry.items.map((item) => (
            <div key={item.id} className={`employee-entry-item ${item.is_completed ? 'completed' : ''}`}>
              <div className="employee-item-header">
                <label className="employee-item-checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!item.is_completed}
                    disabled={!!item.item?.requires_image && !item.image_path && !item.is_completed}
                    onChange={() => handleUpdateEntryItem(item.id, !item.is_completed)}
                    className="employee-item-checkbox"
                  />
                  <span className={`employee-item-title ${item.is_completed ? 'completed' : ''}`}>
                    {item.item?.title || 'İsimsiz Madde'}
                  </span>
                </label>
              </div>

              {item.item?.description && <div className="employee-item-description">{item.item.description}</div>}

              {!!item.item?.requires_image && !item.image_path && !item.is_completed && (
                <div className="employee-item-requirement">Bu madde için görsel yüklenmeden tamamlanamaz.</div>
              )}

              <div className="employee-item-notes-container">
                <textarea
                  value={item.notes || ''}
                  onChange={(e) => handleUpdateEntryItem(item.id, item.is_completed, e.target.value)}
                  placeholder="Notlar..."
                  className="employee-item-notes"
                ></textarea>
              </div>

              {item.item?.requires_image && (
                <div className="employee-item-image-container">
                  {item.image_path ? (
                    <div className="employee-item-image">
                      <img
                        src={getUploadsUrl(item.image_path)}
                        alt="Görev görseli"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        className="employee-btn-upload-file employee-btn-danger employee-remove-image-btn"
                        onClick={() => handleRemoveEntryItemImage(item.id)}
                        disabled={removingItemImageId === item.id || uploadingItemId === item.id}
                      >
                        {removingItemImageId === item.id ? 'Kaldırılıyor...' : 'Görseli Kaldır'}
                      </button>
                    </div>
                  ) : (
                    <div className="employee-item-image-upload">
                      <p>Görsel gerekiyor</p>
                      <div className="employee-image-upload-btn-wrapper">
                        <button className="employee-btn-upload-file" type="button">
                          Görsel Seç
                        </button>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files && e.target.files[0] && handleImageUpload(item.id, e.target.files[0])}
                          disabled={uploadingItemId === item.id}
                          className="employee-image-upload-input"
                        />
                      </div>
                      {uploadingItemId === item.id && <p className="employee-uploading-status">Yükleniyor...</p>}
                    </div>
                  )}
                  {item.image_path && (getImageUploadedByName(item) || item.image_uploaded_at) && (
                    <div className="employee-meta-info">
                      {getImageUploadedByName(item) ? `Görseli yükleyen: ${getImageUploadedByName(item)}` : 'Görsel yüklendi'}
                      {item.image_uploaded_at ? ` • ${new Date(item.image_uploaded_at).toLocaleString('tr-TR')}` : ''}
                    </div>
                  )}
                </div>
              )}

              {item.is_completed && item.completed_at && (
                <div className="employee-completion-info">
                  Tamamlandı: {new Date(item.completed_at).toLocaleString('tr-TR')}
                  {getCompletedByName(item) ? ` • Tamamlayan: ${getCompletedByName(item)}` : ''}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Render the todo list interface
  return (
    <div className="employee-todo-list">
      <div className="employee-section-header">
        <h2>Yapılacaklar</h2>
      </div>
      
      <div className="employee-todo-header">
        <div className="employee-date-selector">
          <label htmlFor="date-picker">Tarih: </label>
          <input 
            type="date" 
            id="date-picker" 
            value={selectedDate} 
            onChange={handleDateChange} 
            max={toYMD(new Date())}
            className="employee-date-input"
          />
        </div>
        
        <div className="employee-completed-toggle">
          <label className="employee-toggle-label">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Tamamlananları göster
          </label>
          <div className="employee-toggle-meta">
            <span className="employee-toggle-pill">{getTotalIncompleteTasks()} açık</span>
            <span className="employee-toggle-pill muted">{getTotalCompletedTasks()} tamamlandı</span>
          </div>
        </div>
      </div>
      
      {/* Error and success messages */}
      {error && <div className="employee-error-message">{error}</div>}
      {success && <div className="employee-success-message">{success}</div>}
      
      {/* Task content in a scrollable container with scroll position preservation */}
      <div className="employee-todo-container" ref={todoContainerRef}>
        {/* Loading indicators */}
        {loading && <div className="employee-loading">Görevler yükleniyor...</div>}
        
        {/* Task content */}
        {!loading && (
          <div className="employee-todo-content">
            {/* Display when no tasks found */}
            {filteredEntries.length === 0 && filteredCustomTasks.length === 0 && (
              <div className="employee-no-tasks">
                <p>
                  {showCompleted ? 'Bu tarih için görev bulunmamaktadır.' : 'Tamamlananlar gizli. Açık görev bulunmamaktadır.'}
                </p>
              </div>
            )}
            
            {/* Custom Tasks List */}
            {filteredCustomTasks.length > 0 && (
              <div className="employee-custom-tasks">
                <h3 className="employee-section-title">
                  Özel Görevler
                  <span className="employee-tasks-remaining">({customTasks.filter(t => !t.is_completed).length} açık)</span>
                </h3>
                
                <div className="employee-task-list">
                  {filteredCustomTasks.map(task => (
                    <div 
                      key={task.id} 
                      className={`employee-task-item ${task.is_completed ? 'completed' : ''}`}
                    >
                      <div className="employee-task-header">
                        <label className="employee-task-checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={!!task.is_completed} 
                            onChange={(e) => handleUpdateCustomTask(task.id, { is_completed: e.target.checked })} 
                            className="employee-task-checkbox" 
                          />
                          <span className={`employee-task-title ${task.is_completed ? 'completed' : ''}`}>
                            {task.title}
                          </span>
                        </label>
                      </div>
                      
                      {task.description && (
                        <div className="employee-task-description">{task.description}</div>
                      )}
                      
                      <div className="employee-task-notes-container">
                        <textarea 
                          value={task.notes || ''} 
                          onChange={(e) => handleUpdateCustomTask(task.id, { notes: e.target.value })} 
                          placeholder="Görev notları..." 
                          className="employee-task-notes"
                        ></textarea>
                      </div>
                      
                      <div className="employee-task-image-container">
                        {task.image_path ? (
                          <div className="employee-task-image">
                            <img 
                              src={getUploadsUrl(task.image_path)} 
                              alt={task.title || 'Görev görseli'} 
                              onError={(e) => {e.target.style.display='none';}} 
                            />
                          </div>
                        ) : (
                          <div className="employee-task-image-upload">
                            <div className="employee-image-upload-btn-wrapper">
                              <button className="employee-btn-upload-file">Görsel Yükle</button>
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={(e) => e.target.files && e.target.files[0] && handleTaskImageUpload(task.id, e.target.files[0])} 
                                disabled={uploadingTaskId === task.id} 
                                className="employee-image-upload-input" 
                              />
                            </div>
                            {uploadingTaskId === task.id && <p className="employee-uploading-status">Yükleniyor...</p>}
                          </div>
                        )}
                      </div>
                      {task.image_path && (
                        <button
                          type="button"
                          className="employee-btn-upload-file employee-btn-danger employee-remove-image-btn"
                          onClick={() => handleRemoveTaskImage(task.id)}
                          disabled={removingTaskImageId === task.id || uploadingTaskId === task.id}
                        >
                          {removingTaskImageId === task.id ? 'Kaldırılıyor...' : 'Görseli Kaldır'}
                        </button>
                      )}
                      
                      {task.image_path && (getImageUploadedByName(task) || task.image_uploaded_at) && (
                        <div className="employee-meta-info">
                          {getImageUploadedByName(task) ? `Görseli yükleyen: ${getImageUploadedByName(task)}` : 'Görsel yüklendi'}
                          {task.image_uploaded_at ? ` • ${new Date(task.image_uploaded_at).toLocaleString('tr-TR')}` : ''}
                        </div>
                      )}

                      {task.is_completed && (task.completed_at || task.updated_at) && (
                        <div className="employee-completion-info">
                          Tamamlandı: {new Date(task.completed_at || task.updated_at).toLocaleString('tr-TR')}
                          {getCompletedByName(task) ? ` • Tamamlayan: ${getCompletedByName(task)}` : ''}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Template-Based Entries */}
            {entries.length > 0 && (
              <div className="employee-template-entries">
                <h3 className="employee-section-title">
                  Şablon Görevleri
                  <span className="employee-tasks-remaining">({entries.reduce((acc, entry) => acc + getEntryCounts(entry).open, 0)} açık)</span>
                </h3>

                {/* Template tabs */}
                {templateTabs.length > 1 && (
                  <div className="employee-template-tabs" role="tablist" aria-label="Şablon sekmeleri">
                    {templateTabs.map((tab) => {
                      const disabled = tab.visibleCount === 0;
                      const meta = `${tab.counts.open}/${tab.counts.total}`;

                      return (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={String(activeTemplateTab) === String(tab.id)}
                          className={`employee-template-tab ${String(activeTemplateTab) === String(tab.id) ? 'active' : ''}`}
                          onClick={() => setActiveTemplateTab(tab.id)}
                          disabled={disabled}
                          title={tab.title}
                        >
                          <span className="employee-template-tab-title">{tab.title}</span>
                          <span className="employee-template-tab-meta">{meta}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                <div className="employee-template-entries-content">
                  {(() => {
                    if (!activeTemplateTab) {
                      return (
                        <div className="employee-no-tasks employee-no-tasks--inner">
                          Bu tarihte şablon görevi yok.
                        </div>
                      );
                    }

                    const selected = filteredEntries.find((e) => String(e.id) === String(activeTemplateTab));
                    if (!selected) {
                      return (
                        <div className="employee-no-tasks employee-no-tasks--inner">
                          Bu şablonda gösterilecek görev yok.
                        </div>
                      );
                    }

                    return renderEntryCard(selected);
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeTodoList;
